/**
 * GIWAHS — Data Hooks (React)
 * ---------------------------------------------------------------------
 * All hooks return { data, loading, error, refetch }.
 *
 * In mock mode the initial render is already populated (loading = false,
 * data = synchronous fallback) so existing pages render with zero flicker.
 *
 * In Supabase mode the initial render shows loading = true and data
 * resolves on first useEffect tick. Pages should always handle the three
 * states to remain mode-agnostic.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import data, { writes, MODE } from '../lib/dataClient';

// ─── core async resource hook ─────────────────────────────────────────
const useResource = (fn, deps, syncFallback) => {
  // In mock mode we synchronously call the fn (it returns a resolved promise
  // wrapping local data); we still keep loading=false from the first render
  // by attempting to extract the value immediately.
  const [state, setState] = useState({
    data: syncFallback ?? null,
    loading: MODE === 'mock' ? false : true,
    error: null,
  });
  const mounted = useRef(true);

  const run = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const result = await fn();
      if (!mounted.current) return;
      setState({ data: result, loading: false, error: null });
    } catch (err) {
      if (!mounted.current) return;
      console.error('[GIWAHS data]', err);
      setState({ data: null, loading: false, error: err });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mounted.current = true;
    run();
    return () => { mounted.current = false; };
  }, [run]);

  return { ...state, refetch: run };
};

// ─── Suppliers ────────────────────────────────────────────────────────
export const useSuppliers = (filters = {}) => {
  const key = JSON.stringify(filters);
  const sync = MODE === 'mock' ? require('../data/mockData').SUPPLIERS : null;
  return useResource(() => data.listSuppliers(filters), [key], sync);
};

export const useSupplier = (slug) => {
  const sync = MODE === 'mock' && slug
    ? require('../data/mockData').getSupplierBySlug(slug) || null
    : null;
  return useResource(() => slug ? data.getSupplierBySlug(slug) : Promise.resolve(null), [slug], sync);
};

export const useFeaturedSuppliers = () => {
  const sync = MODE === 'mock'
    ? require('../data/mockData').SUPPLIERS.filter(s => s.featured)
    : null;
  return useResource(() => data.getFeaturedSuppliers(), [], sync);
};

// ─── Products ─────────────────────────────────────────────────────────
export const useProducts = (filters = {}) => {
  const key = JSON.stringify(filters);
  const sync = MODE === 'mock' ? require('../data/mockData').PRODUCTS : null;
  return useResource(() => data.listProducts(filters), [key], sync);
};

export const useProduct = (slug) => {
  const sync = MODE === 'mock' && slug
    ? require('../data/mockData').getProductBySlug(slug) || null
    : null;
  return useResource(() => slug ? data.getProductBySlug(slug) : Promise.resolve(null), [slug], sync);
};

// ─── Guides ───────────────────────────────────────────────────────────
export const useGuides = (filters = {}) => {
  const key = JSON.stringify(filters);
  const { GUIDES } = require('../data/contentData');
  const sync = MODE === 'mock' ? GUIDES : null;
  return useResource(() => data.listGuides(filters), [key], sync);
};

export const useGuide = (slug) => {
  let sync = null;
  if (MODE === 'mock' && slug) {
    const { GUIDES } = require('../data/contentData');
    const { GUIDE_BODIES } = require('../data/guideBodies');
    const g = GUIDES.find(x => x.slug === slug);
    if (g) sync = { ...g, body: GUIDE_BODIES[slug] || null };
  }
  return useResource(() => slug ? data.getGuideBySlug(slug) : Promise.resolve(null), [slug], sync);
};

// ─── Mutations (lead capture) ─────────────────────────────────────────
export const useMutation = (mutationFn) => {
  const [state, setState] = useState({ data: null, loading: false, error: null, submitted: false });

  const mutate = useCallback(async (payload) => {
    setState({ data: null, loading: true, error: null, submitted: false });
    try {
      const result = await mutationFn(payload);
      setState({ data: result, loading: false, error: null, submitted: true });
      return result;
    } catch (err) {
      console.error('[GIWAHS mutation]', err);
      setState({ data: null, loading: false, error: err, submitted: false });
      throw err;
    }
  }, [mutationFn]);

  const reset = useCallback(() => setState({ data: null, loading: false, error: null, submitted: false }), []);

  return { ...state, mutate, reset };
};

export const useSubmitRfq                 = () => useMutation(writes.submitRfq);
export const useSubmitContact             = () => useMutation(writes.submitContact);
export const useSubmitSupplierApplication = () => useMutation(writes.submitSupplierApplication);
